<script lang="ts" setup>
import type { AuctionFilterState } from '@autobid/nuxt-pinia-store/store/useAuctionStore'
import type { DynamicAuctionList } from '@autobid/strapi-integration/rest/components'
import type { Item } from '@autobid/ui/types/components/AuctionsData'
import type { SocketEvent } from '@autobid/ui/types/websocket/SocketEvent'
import { useAuctionStore } from '@autobid/nuxt-pinia-store/store/useAuctionStore'
import { useWebSocketStore } from '@autobid/nuxt-pinia-store/store/useWebSocketStore'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { useDebounce } from '@autobid/ui/composables/useDebounce'
import { useInfiniteScroll } from '@autobid/ui/composables/useInfiniteScroll'
import { APP_NAME_ID_MAP } from '@autobid/ui/constants/APP_NAME_ID_MAP'
import { AUCTION_SOCKET_EVENTS } from '@autobid/ui/constants/AUCTION_SOCKET_EVENTS'
import { STAGES } from '@autobid/ui/constants/STAGES'
import { auctionListQueryKey } from '@autobid/ui/utils/auctions/auctionListQueryKey'
import { convertListOfAuctions } from '@autobid/ui/utils/auctions/convertDataToDisplay'
import { useAutoAnimate } from '@formkit/auto-animate/vue'
import { useInfiniteQuery, useQueryClient } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { onBeforeUnmount, onMounted } from 'vue'
import AuctionListPlaceholder from './AuctionListPlaceholder.vue'
import AuctionListSpinner from './AuctionListSpinner.vue'
import AuctionListFilter from './filter/AuctionListFilter.vue'
import AuctionListItem from './item/AuctionListItem.vue'
import AuctionListDate from './item/AuctionListItemDate.vue'
import AuctionListNationality from './item/AuctionListItemNationality.vue'

const props = defineProps<Props>()
const [parent] = useAutoAnimate()
const threshold = ref<HTMLElement>()

interface Props {
  data: DynamicAuctionList
}

const { $trpc } = useNuxtApp()
const { startListen } = useWebSocketStore()
const { locale } = useI18n()

provide('isMainPage', props.data.type === 'active')

const auctionStoreKey = JSON.stringify(props)
provide('auctionStoreKey', auctionStoreKey)

type QueryKey = ReturnType<typeof auctionListQueryKey.filtered>

interface FetchParams {
  pageParam: number
  queryKey: QueryKey
}

type AuctionSocketEvent = SocketEvent<{
  auctionId: number
}>

function getStartOfDay(date: string) {
  const startOfDay = new Date(date)
  startOfDay.setHours(0, 0, 0, 0)
  return startOfDay
}

function getEndOfDay(date: string) {
  const endOfDay = new Date(date)
  endOfDay.setHours(23, 59, 59, 999)
  return endOfDay
}

function formatStartDateRange(startDateRange: AuctionFilterState['startDateRange']) {
  return {
    dateTo: startDateRange.dateTo
      ? getEndOfDay(startDateRange.dateTo).toISOString()
      : '',
    dateFrom: startDateRange.dateFrom
      ? getStartOfDay(startDateRange.dateFrom).toISOString()
      : '',
  }
}

function formatStoreFilter(filters: AuctionFilterState) {
  const { designations, startDateRange, descriptionTitles, ..._filter }
    = filters

  const formattedStartDateRange = formatStartDateRange(startDateRange)

  return {
    ..._filter,
    descriptionTitles: descriptionTitles.length ? descriptionTitles : undefined,
    designations: designations.length ? designations : undefined,
    ...formattedStartDateRange,
  }
}

function findAppIds(_apps: string[]) {
  return _apps?.map(app => APP_NAME_ID_MAP[app.toLowerCase() as keyof typeof APP_NAME_ID_MAP])
}

const appIds = computed(() => findAppIds(props.data.apps))
provide('appIds', appIds)

async function fetchAuctions({ pageParam = 0, queryKey }: FetchParams) {
  const filter = queryKey[2]
  const props = queryKey[3]

  const department = props?.data?.filterByDepartment

  const filterFromStore: ReturnType<typeof formatStoreFilter> | undefined
    = props.data.type === 'all' ? formatStoreFilter(filter) : undefined
  const mergedAppIds = [
    ...new Set([...(filterFromStore?.appIds ?? []), ...(appIds.value ?? [])]),
  ]

  try {
    const auctions = await $trpc.auctions.getAuctionsList.query({
      lang: locale.value,
      online: props.data.type === 'active',
      pageNumber: pageParam,
      pageSize: props?.data?.amountActive,
      // filters from store:
      ...(filterFromStore || {}),
      // filters from props:
      department: department?.length ? department : undefined,
      appIds: mergedAppIds.length ? mergedAppIds : undefined,
    })

    const { items, ...page } = auctions

    const dataToDisplay = convertListOfAuctions({
      data: items,
      lang: locale.value,
    })

    return {
      auctions: dataToDisplay,
      page,
    }
  }
  catch (error) {
    const { message, code } = getError(error)
    throw createError({ statusCode: code, statusMessage: message })
  }
}

const { filter, isRendered } = storeToRefs(useAuctionStore(auctionStoreKey))

const queryKey = computed(() => auctionListQueryKey.filtered({ lang: locale.value, filters: filter.value, props }))

const {
  error,
  refetch,
  data: auctions,
  fetchNextPage,
  isFetchingNextPage,
  isInitialLoading,
  isStale,
} = useInfiniteQuery({
  queryKey,
  queryFn: params => fetchAuctions(params as unknown as FetchParams),
  getNextPageParam: (lastPage) => {
    const next = lastPage.page.itemPageOffset + 1
    return next < lastPage.page.itemPageCount ? next : undefined
  },
  refetchOnWindowFocus: false,
  refetchOnMount: false,
})

if (!props?.data?.buttonAuction) {
  useInfiniteScroll(fetchNextPage, isFetchingNextPage, threshold)
}

const dataToDisplay = computed(() => {
  return (
    auctions.value?.pages
      ?.map(page => page.auctions)
      .flat()
      ?.filter(Boolean) ?? []
  )
})

const queryClient = useQueryClient()

const refreshDataInOneReq = useDebounce(async () => {
  if (!auctions.value) {
    return
  }
  const result = await fetchAuctions({
    pageParam: 0,
    queryKey: auctionListQueryKey.filtered({
      lang: locale.value,
      filters: filter.value,
      props: {
        data: {
          ...props.data,
          amountActive:
            auctions.value.pages.length * (props.data.amountActive ?? 10),
        },
      },
    }),
  })

  const paginated = result.auctions.reduce((acc: typeof auctions.value['pages'][number]['auctions'][], current, index) => {
    const currIdx = Math.floor(index / (props.data.amountActive ?? 10))
    const currArr = acc[currIdx] ?? []

    currArr.push(current)
    acc[currIdx] = currArr
    return acc
  }, [])

  const queryKey = auctionListQueryKey.filtered({ lang: locale.value, filters: filter.value, props })
  const queryData = queryClient.getQueryData<typeof auctions.value>(queryKey)

  const updatedQueryData
    = queryData?.pages?.map((page, index) => ({
      ...page,
      auctions: paginated[index],
    })) ?? []

  queryClient.setQueryData(queryKey, {
    pages: updatedQueryData,
    pageParams: queryData?.pageParams,
  })
})

const SOCKET_CALLBACK_NAME = 'auction-list'
function auctionSocket() {
  const invalidateFilter = () => {
    queryClient.invalidateQueries(['auctionListFilter', locale.value, appIds])
  }

  function invalidateList() {
    queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
  }

  const isMainPage = props.data.type === 'active'

  const remove = (e: AuctionSocketEvent) => {
    if (isMainPage || isStale.value) {
      invalidateList()
      invalidateFilter()
      return
    }

    queryClient.setQueriesData(auctionListQueryKey.all(), (item) => {
      const queryData = item as typeof auctions.value

      const newPages
        = queryData?.pages?.map((page) => {
          const auctions = page.auctions?.filter((auction) => {
            return auction.number !== e.context.auctionId
          })

          return {
            ...page,
            auctions,
          }
        }) ?? []

      return { ...queryData, pages: newPages }
    })

    invalidateFilter()
  }

  const updateAuctionData = (auctionId: number, data: Partial<Item>) => {
    if ((isMainPage && data.stage) || isStale.value) {
      invalidateList()
      invalidateFilter()
      return
    }

    queryClient.setQueriesData(auctionListQueryKey.all(), (item: unknown) => {
      const queryData = item as typeof auctions.value | undefined

      if (!queryData) {
        return queryData
      }

      const newPages
        = queryData?.pages?.map((page) => {
          const auctions = page.auctions
            ?.map((auction) => {
              if (auction.number === auctionId) {
                return {
                  ...auction,
                  ...data,
                }
              }
              return auction
            })
            .sort((a, b) => {
              return STAGES.indexOf(a.stage) - STAGES.indexOf(b.stage)
            })
          return {
            ...page,
            auctions,
          }
        }) ?? []

      return { ...queryData, pages: newPages }
    })

    invalidateFilter()
  }

  function handleInvalidate() {
    invalidateList()
    invalidateFilter()
  }

  startListen({
    eventName: AUCTION_SOCKET_EVENTS.hidden,
    callbackName: SOCKET_CALLBACK_NAME,
    callback: remove,
  })

  startListen({
    eventName: AUCTION_SOCKET_EVENTS.withdraw,
    callbackName: SOCKET_CALLBACK_NAME,
    callback: remove,
  })

  startListen({
    eventName: AUCTION_SOCKET_EVENTS.inPrepare,
    callbackName: SOCKET_CALLBACK_NAME,
    callback: handleInvalidate,
  })

  startListen({
    eventName: AUCTION_SOCKET_EVENTS.finished,
    callbackName: SOCKET_CALLBACK_NAME,
    callback: handleInvalidate,
  })

  startListen({
    eventName: AUCTION_SOCKET_EVENTS.hotbidStart,
    callbackName: SOCKET_CALLBACK_NAME,
    callback: (event) => {
      updateAuctionData(event.context.auctionId, { inActiveHotbidPhase: true })
    },
  })
}

function recoverFromError() {
  if (!dataToDisplay.value?.length) {
    return refetch()
  }
  return fetchNextPage()
}

onMounted(() => {
  isRendered.value = true
  auctionSocket()
})

watch(isStale, () => {
  if (isStale.value) {
    refreshDataInOneReq()
  }
}, { immediate: true })

onBeforeUnmount(() => {
  isRendered.value = false
})
</script>

<template>
  <div
    class="relative z-20 my-4 flex flex-col-reverse lg:block lg:bg-gray-100 lg:p-4"
  >
    <div class="container relative z-20 mx-auto w-full text-primary @container">
      <AuctionListFilter v-if="data.showFilter" />
      <div ref="parent" class="mx-auto flex flex-col">
        <AuctionListPlaceholder
          v-if="isInitialLoading"
          :size="data.amountActive ?? 10"
        />
        <h2
          v-if="!isInitialLoading && !dataToDisplay?.length && !error"
          class="p-2 md:p-0"
        >
          {{ $t('auction-list.no-auctions') }}
        </h2>
        <div v-for="(item, index) in dataToDisplay" :key="item.number">
          <template v-if="data.type === 'all'">
            <AuctionListNationality
              :prev-nationality="dataToDisplay[index - 1]?.nationality"
              :current-nationality="item.nationality"
              :index="index"
              :current-stage="item.stage"
              :prev-stage="dataToDisplay[index - 1]?.stage"
            />
            <AuctionListDate
              :prev-date="dataToDisplay[index - 1]?.startDate"
              :stage="item.stage"
              :current-date="item.startDate"
              :index="index"
            />
          </template>
          <AuctionListItem v-bind="item" />
        </div>
        <AuctionListSpinner v-if="isFetchingNextPage" />
        <div ref="threshold" />
        <div v-if="error" role="alert" class="py-4">
          {{ $t('auction-list.fetch-failed') }}
          <span class="text-xs">({{ error }})</span>
          <LazyElementsButton @click="recoverFromError">
            {{
              $t('auction-list.refetch')
            }}
          </LazyElementsButton>
        </div>
      </div>
    </div>
    <div class="px-2 md:px-0">
      <!-- @vue-expect-error - color is an enum in the strapi, since the button accepts the string -->
      <ElementsButton
        v-if="data?.buttonAuction"
        class="my-1 flex items-center justify-center lg:mt-4"
        :target="data.buttonAuction.newTab ? '_blank' : undefined"
        v-bind="data.buttonAuction"
        type="block"
      >
        {{ data.buttonAuction.text }}
      </ElementsButton>
    </div>
  </div>
</template>
